import currency from "currency.js";
import { find } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { notifications } from "@/app/container/notifications";
import { useAppContext } from "@/app/context";
import { useTransactionFee } from "@/app/hooks/use-transaction-fee";
import {
  CounterOffersInOfferGroupDocument,
  MarketOrdersDocument,
  OfferPosition,
  OrderPosition,
  RegistryCode,
  useCreateOfferMutation,
  useTenantsQuery,
} from "@/app/types/generated/graphql";
import { TraceXSupportedRegistries } from "@/app/types/okta.types";

import { CounterSteps, CreateCounterOfferHookProps, FormValues } from "./create-counter-offer-modal.types";

export const modalSubTitles = {
  [CounterSteps.COUNTER_FORM]: "Create a counter against this ask.",
  [CounterSteps.COUNTER_SUBMIT]: `By submitting this counter offer you are agreeing that the counterparty may accept it and if accepted the order will be executed immediately at the offered price.`,
  [CounterSteps.COUNTER_SUCCESS]: `Your offer has been received by the seller.`,
};

export const useCreateCounterOfferModal = ({
  visible,
  closeModal,
  counteredTableData,
  tenantID,
  counterFromID,
  offerGroupId,
  counteredVolume,
  offerId,
}: CreateCounterOfferHookProps) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<CounterSteps>(CounterSteps.COUNTER_FORM);
  const { userSelections } = useAppContext();
  const registryCode = userSelections?.registry?.registryCode as TraceXSupportedRegistries;
  const isActiveRegistryErcot = registryCode === RegistryCode.Ercot;

  const [createOffer] = useCreateOfferMutation({
    refetchQueries: [
      MarketOrdersDocument, // Use a common query document after counter offer is implemented for ERCOT as well
      // This will only refetch the query associated with the particular offerGroupId.
      ...(offerGroupId
        ? [
            {
              query: CounterOffersInOfferGroupDocument,
              variables: {
                filter: {
                  offerGroupId,
                  registryCode,
                },
              },
            },
          ]
        : []),
    ],
  });

  const { data: tenantsData } = useTenantsQuery();

  const { getAppliedFee } = useTransactionFee({ registryCode });

  const fee = getAppliedFee(OrderPosition.Bid);

  const modalSubTitle = modalSubTitles[currentStep];

  const handleStepChange = useCallback(
    (step: CounterSteps) => {
      //when counter offer to an offer is successfully then we close the modal and refetch offersInOfferGroup
      // On the market page(offerId is null), show success modal after placing a counter offer
      if (step === CounterSteps.COUNTER_SUCCESS && offerId) return closeModal();
      setCurrentStep(step);
    },
    [closeModal, offerId],
  );

  const currencySymbol = useMemo(() => {
    const tenant = find(tenantsData?.tenants, { id: tenantID });
    return tenant?.localisation?.currencySymbol ?? "$";
  }, [tenantID, tenantsData]);

  const onCounterConfirm = useCallback(
    async (values: FormValues) => {
      try {
        const { data } = await createOffer({
          variables: {
            input: {
              fromAccountId: counterFromID,
              toAccountId: counteredTableData.account?.id,
              volume: Number(values.counterVolume),
              unitPrice: currency(values.counterPrice).intValue.toString(),
              position: OfferPosition.Bid,
              registryCode,
              orderId: counteredTableData.id,
              offerId,
            },
          },
        });
        if (data?.createOffer.status) {
          handleStepChange(CounterSteps.COUNTER_SUCCESS);
        }
      } catch (error) {
        error instanceof Error &&
          notifications.error({
            description: t(error.message),
          });
      }
    },
    [
      handleStepChange,
      t,
      createOffer,
      counterFromID,
      counteredTableData.account?.id,
      registryCode,
      counteredTableData.id,
      offerId,
    ],
  );

  return {
    visible,
    closeModal,
    counteredTableData,
    modalSubTitle,
    currentStep,
    handleStepChange,
    currencySymbol,
    onCounterConfirm,
    fee,
    counteredVolume,
    isActiveRegistryErcot,
  };
};
